import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AuthService, authErrorKey } from '../../services/auth.service';
import { RouterModule } from '@angular/router';
import { BrandLogoComponent } from '../../components/brand-logo/brand-logo.component';
import { BrandedLoaderComponent } from '../../components/branded-loader/branded-loader.component';
import { AuthAlertComponent } from '../../components/auth-alert/auth-alert.component';
import { MIN_PASSWORD_LENGTH, fieldError, passwordsMatchValidator } from '../../utils/password';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe, RouterModule, BrandLogoComponent, BrandedLoaderComponent, AuthAlertComponent],
  templateUrl: './register.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);

  readonly minPasswordLength = MIN_PASSWORD_LENGTH;
  readonly fieldError = fieldError;

  registerForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)]],
    confirmPassword: ['', Validators.required],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    dob: ['', Validators.required],
  }, { validators: passwordsMatchValidator });

  errorMessage = signal<string | null>(null);
  isLoading = signal(false);
  /** Address the confirmation email was sent to, once the account is created. */
  registeredEmail = signal<string | null>(null);
  isResending = signal(false);
  resent = signal(false);

  async onSubmit() {
    if (this.isLoading()) return;
    this.registerForm.markAllAsTouched();
    this.errorMessage.set(null);
    const { email, password, firstName, lastName, dob } = this.registerForm.getRawValue();
    if (this.registerForm.invalid || !email || !password || !firstName || !lastName || !dob) return;

    const trimmedEmail = email.trim();
    const first = firstName.trim();
    const last = lastName.trim();
    this.isLoading.set(true);
    try {
      // The metadata feeds the handle_new_user trigger, which creates the profile.
      const { data, error } = await this.authService.signUp(trimmedEmail, password, {
        first_name: first,
        last_name: last,
        dob,
        full_name: `${first} ${last}`,
      });

      if (error) {
        this.errorMessage.set(authErrorKey(error));
      } else if (data.user && data.user.identities?.length === 0) {
        // Supabase answers an existing, confirmed address with a user that has no identity.
        this.errorMessage.set('auth.error.userAlreadyExists');
      } else if (data.user) {
        this.registeredEmail.set(trimmedEmail);
      }
    } catch (err) {
      console.error('Unexpected sign-up error:', err);
      this.errorMessage.set('auth.error.unexpected');
    } finally {
      this.isLoading.set(false);
    }
  }

  async resendConfirmation() {
    const email = this.registeredEmail();
    if (!email || this.isResending()) return;
    this.isResending.set(true);
    this.errorMessage.set(null);
    try {
      const { error } = await this.authService.resendConfirmation(email);
      if (error) this.errorMessage.set(authErrorKey(error));
      else this.resent.set(true);
    } finally {
      this.isResending.set(false);
    }
  }
}
